import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { X, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const AttachmentPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [attachmentInfo, setAttachmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const blobUrlRef = useRef(null);

  const extractFileName = (contentDisposition) => {
    if (!contentDisposition) {
      return null;
    }
    const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (filenameStarMatch && filenameStarMatch[1]) {
      return decodeURIComponent(filenameStarMatch[1]);
    }
    const filenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1];
    }
    return null;
  };

  useEffect(() => {
    const fetchAttachment = async () => {
      try {
        setLoading(true);
        
        // Pobierz załącznik jako blob i utwórz blob URL dla podglądu
        const downloadResponse = await api.get(`/invoices/attachments/${id}/download`, {
          responseType: 'blob'
        });

        const contentType = downloadResponse.headers['content-type'] || 'application/octet-stream';
        const contentDisposition = downloadResponse.headers['content-disposition'] || '';
        const fileName = extractFileName(contentDisposition) || `zalacznik-${id}`;

        const blob = new Blob([downloadResponse.data], { type: contentType });
        const blobUrl = window.URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setAttachmentUrl(blobUrl);
        setAttachmentInfo({
          id: Number(id),
          originalFileName: fileName,
          fileType: contentType,
          fileSize: downloadResponse.data?.size
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching attachment:', err);
        console.error('Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message
        });
        
        if (err.response?.status === 401) {
          setError('Brak autoryzacji. Zaloguj się ponownie.');
        } else if (err.response?.status === 403) {
          setError('Brak uprawnień do wyświetlenia tego załącznika.');
        } else if (err.response?.status === 404) {
          setError('Załącznik nie został znaleziony.');
        } else {
          setError('Błąd podczas pobierania załącznika: ' + (err.response?.data?.error || err.message));
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAttachment();
    }
    
    // Cleanup: zwolnij blob URL przy odmontowaniu komponentu
    return () => {
      if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
        window.URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [id]);

  const handleClose = () => {
    // Jeśli otwarte w nowym oknie, zamknij je
    if (window.opener) {
      window.close();
    } else {
      // Jeśli otwarte w tej samej karcie, wróć do poprzedniej strony
      navigate(-1);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/invoices/attachments/${id}/download`, {
        responseType: 'blob'
      });
      
      // Utwórz URL dla blob
      const blob = new Blob([response.data], { 
        type: attachmentInfo?.fileType || 'application/octet-stream' 
      });
      const url = window.URL.createObjectURL(blob);
      
      // Pobierz plik
      const link = document.createElement('a');
      link.href = url;
      link.download = attachmentInfo?.originalFileName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Zwolnij pamięć
      window.URL.revokeObjectURL(url);
      
      toast.success('Załącznik pobrany');
    } catch (error) {
      console.error('Błąd podczas pobierania załącznika:', error);
      toast.error('Błąd podczas pobierania załącznika');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Błąd</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isImage = attachmentInfo?.fileType?.startsWith('image/');
  const isPdf = attachmentInfo?.fileType === 'application/pdf';
  const canPreview = isImage || isPdf;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header z przyciskiem zamknięcia */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center space-x-4">
          <h1 className="text-lg font-semibold text-gray-900">Podgląd załącznika</h1>
          <span className="text-sm text-gray-500 truncate max-w-md" title={attachmentInfo?.originalFileName}>
            {attachmentInfo?.originalFileName}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleDownload}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-2"
            title="Pobierz załącznik"
          >
            <Download className="h-4 w-4" />
            Pobierz
          </button>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Zawartość załącznika */}
      <div className="p-4">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {canPreview ? (
            <div className="w-full" style={{ minHeight: 'calc(100vh - 120px)' }}>
              {isImage ? (
                <img
                  src={attachmentUrl}
                  alt={attachmentInfo?.originalFileName}
                  className="w-full h-auto max-h-[calc(100vh-120px)] object-contain mx-auto"
                  style={{ display: 'block' }}
                />
              ) : isPdf ? (
                <iframe
                  title="Attachment Preview"
                  src={attachmentUrl}
                  className="w-full border-0"
                  style={{ minHeight: 'calc(100vh - 120px)' }}
                />
              ) : null}
            </div>
          ) : (
            <div className="flex items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 120px)' }}>
              <div className="text-center">
                <div className="text-gray-400 text-5xl mb-4">📄</div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Podgląd niedostępny</h2>
                <p className="text-gray-600 mb-6">
                  Ten typ pliku ({attachmentInfo?.fileType || 'nieznany'}) nie może być wyświetlony w przeglądarce.
                </p>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 mx-auto"
                >
                  <Download className="h-4 w-4" />
                  Pobierz plik
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreview;
